/**
 * ESC/POS to HTML converter for preview rendering
 * Parses ESC/POS commands and converts them to HTML for display
 */

export class EscPosToHtmlConverter {
  private html: string = ''
  private currentStyles: {
    bold: boolean
    underline: boolean
    size: 'normal' | 'double-width' | 'double-height' | 'double'
    align: 'left' | 'center' | 'right'
  } = {
    bold: false,
    underline: false,
    size: 'normal',
    align: 'left'
  }
  
  /**
   * Convert ESC/POS byte array to HTML for preview
   */
  convert(data: Uint8Array): string {
    const decoder = new TextDecoder('utf-8', { fatal: false })
    const text = decoder.decode(data)
    
    this.html = ''
    this.resetStyles()
    
    let i = 0
    while (i < text.length) {
      // Check for ESC/POS commands
      if (text[i] === '\x1B' || text[i] === '\x1D') {
        const commandLength = this.parseCommand(text, i)
        i += commandLength
      } else if (text[i] === '\n') {
        // Add line break while preserving current styles
        this.addText('')  // Close any open tags
        this.html += '<br>'
        i++
      } else {
        // Regular text
        let textEnd = i
        while (textEnd < text.length && text[textEnd] !== '\x1B' && text[textEnd] !== '\x1D' && text[textEnd] !== '\n') {
          textEnd++
        }
        
        const textContent = text.substring(i, textEnd)
        this.addText(textContent)
        i = textEnd
      }
    }
    
    // Close any open tags
    this.closeCurrentTags()
    
    return this.html
  }
  
  private parseCommand(text: string, index: number): number {
    const cmd = text[index]
    
    if (cmd === '\x1B') {
      // ESC commands
      if (index + 2 < text.length) {
        const cmd2 = text[index + 1]
        const param = text[index + 2]
        
        switch (cmd2) {
          case '\x40': // Initialize
            this.resetStyles()
            return 2
            
          case '\x45': // Bold
            this.setBold(param === '\x01')
            return 3
            
          case '\x2D': // Underline
            this.setUnderline(param === '\x01')
            return 3
            
          case '\x61': // Alignment
            if (param === '\x00') this.setAlign('left')
            else if (param === '\x01') this.setAlign('center')
            else if (param === '\x02') this.setAlign('right')
            return 3
            
          case '\x74': // Character set / code page
            return 3
            
          default:
            return 2
        }
      }
    } else if (cmd === '\x1D') {
      // GS commands
      if (index + 2 < text.length) {
        const cmd2 = text[index + 1]
        const param = text[index + 2]
        
        switch (cmd2) {
          case '\x21': // Text size
            if (param === '\x00') this.setSize('normal')
            else if (param === '\x10') this.setSize('double-width')
            else if (param === '\x01') this.setSize('double-height')
            else if (param === '\x11') this.setSize('double')
            return 3
            
          case '\x56': // Cut paper
            this.html += '<hr style="border-style: dashed; margin: 20px 0;">'
            return 3
            
          default:
            return 2
        }
      }
    }
    
    return 1
  }
  
  private resetStyles(): void {
    this.closeCurrentTags()
    this.currentStyles = {
      bold: false,
      underline: false,
      size: 'normal',
      align: 'left'
    }
  }
  
  private setBold(enabled: boolean): void {
    if (this.currentStyles.bold !== enabled) {
      this.closeCurrentTags()
      this.currentStyles.bold = enabled
    }
  }
  
  private setUnderline(enabled: boolean): void {
    if (this.currentStyles.underline !== enabled) {
      this.closeCurrentTags()
      this.currentStyles.underline = enabled
    }
  }
  
  private setSize(size: 'normal' | 'double-width' | 'double-height' | 'double'): void {
    if (this.currentStyles.size !== size) {
      this.closeCurrentTags()
      this.currentStyles.size = size
    }
  }
  
  private setAlign(align: 'left' | 'center' | 'right'): void {
    if (this.currentStyles.align !== align) {
      this.closeCurrentTags()
      this.currentStyles.align = align
    }
  }
  
  private addText(text: string): void {
    if (!text) return
    
    // Handle each line separately to ensure proper alignment
    const lines = text.split('\n')
    
    lines.forEach((line, index) => {
      if (index > 0) this.html += '<br>'
      if (!line) return
      
      // Open necessary tags
      let openTags = ''
      let closeTags = ''
      
      // For centered/right text, wrap each line
      if (this.currentStyles.align !== 'left') {
        openTags += `<div style="text-align: ${this.currentStyles.align}; margin: 0;">`
        closeTags = '</div>' + closeTags
      }
      
      // Size styling - simulate thermal printer behavior
      if (this.currentStyles.size !== 'normal') {
        const sizeStyles = {
          'double-width': 'font-size: 20px; font-weight: bold; letter-spacing: 0.05em;',
          'double-height': 'font-size: 20px; line-height: 1.2;',
          'double': 'font-size: 20px; font-weight: bold; letter-spacing: 0.05em;'
        }
        openTags += `<span style="${sizeStyles[this.currentStyles.size]}">`
        closeTags = '</span>' + closeTags
      }
      
      // Bold
      if (this.currentStyles.bold) {
        openTags += '<strong style="font-weight: 900;">'
        closeTags = '</strong>' + closeTags
      }
      
      // Underline
      if (this.currentStyles.underline) {
        openTags += '<u>'
        closeTags = '</u>' + closeTags
      }
      
      // HTML escape the text and preserve spaces
      const escapedText = line
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/ /g, '&nbsp;')
      
      this.html += openTags + escapedText + closeTags
    })
  }
  
  private closeCurrentTags(): void {
    // Nothing to close in this implementation as we wrap each text segment
  }
}

/**
 * Convert ESC/POS data to HTML for preview
 */
export function escPosToHtml(data: Uint8Array): string {
  const converter = new EscPosToHtmlConverter()
  return converter.convert(data)
}